/* eslint-disable react-hooks/exhaustive-deps */
import { Box, ThemeProvider, createTheme } from '@mui/material';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import React, { useEffect, useState } from 'react';

import FullscreenSpinner from 'modules/common/fullscreenSpinner.component';

import { COLOR_PRIMARY, COLOR_SECONDARY, COLOR_WARNING } from 'styles/theme';
import {
  getAuthClientPolicies,
  getAuthorizationSummaries,
  getAuthorizationSummary,
  getEmployeeInfo,
  setAuthId
} from '../../../store/actions/authorizations.actions';
import { getClientDetails, setClientId, setLoadingComplete } from 'modules/clients/clients.actions';
import { showToast } from 'modules/layout/layout.actions';

import ExpenseLineItemDetails from './expenseLineItemDetailsView.component';
import { getVendors } from 'modules/authorizations/store/actions/dataManagement.actions';
import { fetchAuthDetails, fetchEnumerations } from 'modules/authorizations/utils/fetchDataDetailsService';

import {
  getAllCountries,
  getAllCurrencies,
  getAllEnumerations,
  getAllProvinces,
  getAllSaleTypes,
  getAllWeightTypes
} from 'modules/enumerations/store/enumerationActions';
import {
  createApprovedCustomTax,
  deleteApprovedCustomTax,
  getApprovedLineItem,
  getApprovedLineItemSummaries,
  getExpenseByAuthorization,
  updateApprovedCustomTax,
  updateApprovedLineItem
} from 'modules/expenses/store/expenses.actions';

const ExpenseLineItemDetailsView = props => {
  const { authorizationDetails, match, history } = props;

  const [approvedLineItem, setApprovedLineItemDetails] = useState(null);
  const [approvedLineItemCustomTaxes, setApprovedLineItemCustomTaxes] = useState([]);

  const theme = createTheme({
    palette: {
      primary: COLOR_PRIMARY,
      secondary: COLOR_SECONDARY,
      warning: COLOR_WARNING
    }
  });

  const authIdParams = match?.params?.id;
  const authId = Number.parseInt(authIdParams, 10);
  const expenseLineIdParam = match?.params?.expenseLineId ?? '0';
  const approvedExpenseLineId = Number.parseInt(expenseLineIdParam, 10);
  const details = authorizationDetails ? authorizationDetails[authId] : null;

  const viewOnly = details?.approvedLineItem?.isLocked ?? false;

  const [isLoading, setIsLoading] = useState(false);

  const toUiCustomTaxes = list => (Array.isArray(list) ? list.map(t => ({ ...t, isCustomTax: true })) : []);

  useEffect(() => {
    const run = async () => {
      setIsLoading(true);
      try {
        if (authId != null) props.setAuthId(authId);
        await fetchAuthDetails(details, authId, props);
        await fetchEnumerations(props);
        if (!details || !Array.isArray(details.expenses)) {
          await props.getExpenseByAuthorization(authId);
        }
        if (!details || !Array.isArray(details.approvedLineItems)) {
          await props.getApprovedLineItemSummaries(authId);
        }
        if (!approvedLineItem) {
          const approveLineItemResp = await props.getApprovedLineItem(authId, approvedExpenseLineId);
          const response = approveLineItemResp?.response;
          if (response) {
            setApprovedLineItemCustomTaxes(toUiCustomTaxes(response.customTaxes || []));
            setApprovedLineItemDetails(response.approvedLineItem);
          }
        }
      } finally {
        props.setLoadingComplete();
        setIsLoading(false);
      }
    };
    run();
  }, [authId]);

  const updateApprovedLineItemDetails = async item => {
    if (!authId || !approvedExpenseLineId) return;
    await props.updateApprovedLineItem(authId, approvedExpenseLineId, item ?? approvedLineItem);
    const refreshed = await props.getApprovedLineItem(authId, approvedExpenseLineId);
    const response = refreshed?.response;
    if (response) {
      setApprovedLineItemCustomTaxes(toUiCustomTaxes(response.customTaxes || []));
      setApprovedLineItemDetails(response.approvedLineItem);
      return true;
    }
    return false;
  };

  const createCustomTax = async tax => {
    if (!authId || !approvedExpenseLineId) return;
    tax.approvedExpenseLineItemId = approvedExpenseLineId;
    await props.createApprovedCustomTax(authId, approvedExpenseLineId, tax);
    const refreshed = await props.getApprovedLineItem(authId, approvedExpenseLineId);
    const response = refreshed?.response;
    if (response) {
      setApprovedLineItemCustomTaxes(toUiCustomTaxes(response.customTaxes || []));
      setApprovedLineItemDetails(response.approvedLineItem);
      return true;
    }
    return false;
  };

  const updateCustomTax = async tax => {
    if (!authId || !approvedExpenseLineId || !tax?.customTaxesId) return;
    tax.approvedExpenseLineItemId = approvedExpenseLineId;
    await props.updateApprovedCustomTax(authId, approvedExpenseLineId, tax.customTaxesId, tax);
    const refreshed = await props.getApprovedLineItem(authId, approvedExpenseLineId);
    const response = refreshed?.response;
    if (response) {
      setApprovedLineItemCustomTaxes(toUiCustomTaxes(response.customTaxes || []));
      setApprovedLineItemDetails(response.approvedLineItem);
      return true;
    }
    return false;
  };

  const deleteCustomTax = async taxId => {
    if (!authId || !approvedExpenseLineId || !taxId) return;
    await props.deleteApprovedCustomTax(authId, approvedExpenseLineId, taxId);
    const refreshed = await props.getApprovedLineItem(authId, approvedExpenseLineId);
    const response = refreshed?.response;
    if (response) {
      setApprovedLineItemCustomTaxes(toUiCustomTaxes(response.customTaxes || []));
      setApprovedLineItemDetails(response.approvedLineItem);
      return true;
    }
    return false;
  };

  return (
    <ThemeProvider theme={theme}>
      <Box>
        {!isLoading && details && (
          <ExpenseLineItemDetails
            details={details}
            expenseLineId={approvedExpenseLineId}
            location={props.location}
            history={history}
            enumerations={props.enumerations}
            approvedLineItem={approvedLineItem}
            approvedLineItemCustomTaxes={approvedLineItemCustomTaxes}
            authId={authId}
            onUpdateApprovedLineItemDetails={updateApprovedLineItemDetails}
            onCreateCustomTax={createCustomTax}
            onUpdateCustomTax={updateCustomTax}
            onDeleteCustomTax={deleteCustomTax}
            viewOnly={viewOnly}
          />
        )}
        {isLoading && <FullscreenSpinner />}
      </Box>
    </ThemeProvider>
  );
};

const mapStateToProps = state => {
  const { authorizationDetails, isLoading, authorizationSummaries, vendors } = state.authorizations;
  const { unmappedClients, clients, clientsInfo } = state.clients;
  const enumerations = state.enumerations;
  return {
    authorizationDetails,
    isLoading,
    authorizationSummaries,
    unmappedClients,
    vendors,
    enumerations,
    clients,
    clientsInfo
  };
};

export default compose(
  connect(mapStateToProps, {
    getAuthClientPolicies,
    getEmployeeInfo,
    setAuthId,
    setLoadingComplete,
    showToast,
    getAuthorizationSummaries,
    getVendors,
    getAuthorizationSummary,
    getAllEnumerations,
    getAllSaleTypes,
    getAllWeightTypes,
    getAllProvinces,
    getAllCurrencies,
    getAllCountries,
    getExpenseByAuthorization,
    getApprovedLineItemSummaries,
    deleteApprovedCustomTax,
    updateApprovedCustomTax,
    createApprovedCustomTax,
    updateApprovedLineItem,
    getApprovedLineItem,
    getClientDetails,
    setClientId
  })
)(ExpenseLineItemDetailsView);
